import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';
import SubscriptionModal from '../components/SubscriptionModal';
import SellerCard from '../components/SellerCard';
import toast from 'react-hot-toast';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

export default function BarterDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [listing, setListing] = useState(null);
  const [myListings, setMyListings] = useState([]);
  const [selectedListingId, setSelectedListingId] = useState('');
  const [loading, setLoading] = useState(true);
  const [proposing, setProposing] = useState(false);
  const [error, setError] = useState('');
  const [showMessage, setShowMessage] = useState(false);
  const [messageBody, setMessageBody] = useState('');
  const [messageSending, setMessageSending] = useState(false);
  const [showSubscription, setShowSubscription] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [showPaymentConfirm, setShowPaymentConfirm] = useState(false);
  const [selectingPaymentOption, setSelectingPaymentOption] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch target listing
        const { data: listingData } = await API.get(`/listings/${id}`);
        setListing(listingData.listing);

        // If user is logged in, fetch their barter listings
        if (user) {
          try {
            const { data: myBartersData } = await API.get('/barters/listings', {
              params: { limit: 100 }
            });
            // Filter to only show this user's listings
            const userListings = myBartersData.listings.filter(l => l.seller_id === user.id);
            setMyListings(userListings);
          } catch (err) {
            console.error('Failed to fetch user listings:', err);
          }
        }
      } catch (err) {
        setError('Listing not found');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, user]);

  const handleProposeClick = () => {
    if (!selectedListingId) {
      toast.error('Please select an item to trade');
      return;
    }
    setShowPricingModal(true);
  };

  const executeProposeTrace = async () => {
    setProposing(true);
    try {
      const { data } = await API.post('/barters/request', {
        initiatorListingId: parseInt(selectedListingId),
        responderListingId: parseInt(id)
      });

      toast.success('Trade proposal sent!');
      setShowPricingModal(false);
      setTimeout(() => navigate('/dashboard/barter'), 1500);
    } catch (err) {
      if (err.response?.data?.error === 'subscription_required') {
        setShowSubscription(true);
        setShowPricingModal(false);
      } else {
        toast.error(err.response?.data?.error || 'Failed to propose trade');
      }
    } finally {
      setProposing(false);
      setSelectingPaymentOption(false);
    }
  };

  const handleSendMessage = async () => {
    if (!messageBody.trim() || !user) return;

    setMessageSending(true);
    try {
      await API.post('/messages', {
        recipientId: listing.seller_id,
        listingId: id,
        body: messageBody
      });

      toast.success('Message sent!');
      setMessageBody('');
      setShowMessage(false);
    } catch (err) {
      toast.error('Failed to send message');
    } finally {
      setMessageSending(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }} className="float-anim">🍒</div>
          <div style={{ fontSize: 14, color: 'var(--text-soft)' }}>Loading...</div>
        </div>
      </div>
    );
  }

  if (error || !listing) {
    return (
      <div style={{ maxWidth: 700, margin: '0 auto', padding: '40px 48px' }}>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 24, marginBottom: 12 }}>
            Item not found
          </h2>
          <button className="btn btn-cherry" onClick={() => navigate('/barter')}>
            ← Back to Barter Items
          </button>
        </div>
      </div>
    );
  }

  const images = listing.images?.length ? listing.images : [{ url: null }];

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '40px 48px 80px' }}>
      <button
        className="btn btn-ghost btn-sm"
        onClick={() => navigate('/barter')}
        style={{ marginBottom: 24 }}
      >
        ← Back to Barter Items
      </button>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 48, alignItems: 'start' }}>
        {/* Images */}
        <div>
          <div
            style={{
              borderRadius: 16,
              overflow: 'hidden',
              marginBottom: 16,
              aspectRatio: '1',
              background: 'var(--cream)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {images[0]?.url ? (
              <img
                src={`${API_BASE}${images[0].url}`}
                alt={listing.title}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <span style={{ fontSize: 80, opacity: 0.3 }}>📦</span>
            )}
          </div>

          {images.length > 1 && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {images.slice(1).map((img, i) => (
                <div
                  key={i}
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: 10,
                    overflow: 'hidden',
                    background: 'var(--cream)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {img.url ? (
                    <img src={`${API_BASE}${img.url}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <span style={{ fontSize: 32, opacity: 0.3 }}>📦</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Details & Trade */}
        <div>
          <div style={{ marginBottom: 32 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-soft)', marginBottom: 8, textTransform: 'uppercase' }}>
              {listing.category_icon} {listing.category_label}
            </div>
            <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 28, fontWeight: 700, marginBottom: 12 }}>
              {listing.title}
            </h1>
            <p style={{ fontSize: 15, color: 'var(--text-mid)', lineHeight: 1.7, marginBottom: 24 }}>
              {listing.description}
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
              {listing.condition && (
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text-soft)', fontWeight: 700, marginBottom: 4 }}>Condition</div>
                  <div style={{ fontWeight: 600 }}>{listing.condition}</div>
                </div>
              )}
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-soft)', fontWeight: 700, marginBottom: 4 }}>Location</div>
                <div style={{ fontWeight: 600 }}>📍 {listing.location_city}, {listing.location_state}</div>
              </div>
            </div>
          </div>

          {/* Enhanced Seller Card with Reputation */}
          {listing.seller_id && (
            <SellerCard
              userId={listing.seller_id}
              onMessage={() => setShowMessage(true)}
            />
          )}

          {/* Trade Section */}
          {user && user.id !== listing.seller_id ? (
            <div style={{ background: '#FFF3E0', border: '2px solid #FF9800', borderRadius: 14, padding: 20 }}>
              <h3 style={{ fontWeight: 700, fontSize: 16, marginBottom: 16 }}>🔄 Propose a Trade</h3>
              <p style={{ fontSize: 13, color: 'var(--text-mid)', marginBottom: 8 }}>
                Select one of your barter items to trade for this item ($10 fee applies):
              </p>

              {myListings.length > 0 ? (
                <>
                  <select
                    value={selectedListingId}
                    onChange={e => setSelectedListingId(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: 10,
                      border: '1px solid var(--border)',
                      marginBottom: 16,
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: 14
                    }}
                  >
                    <option value="">Select your item to trade...</option>
                    {myListings.map(listing => (
                      <option key={listing.id} value={listing.id}>
                        {listing.title}
                      </option>
                    ))}
                  </select>

                  <button
                    className="btn btn-full"
                    style={{ background: '#FF9800', color: 'white', border: 'none' }}
                    onClick={handleProposeClick}
                    disabled={!selectedListingId}
                  >
                    🔄 Propose Trade
                  </button>
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '20px', background: '#FFF3E0', borderRadius: 10 }}>
                  <p style={{ fontSize: 14, color: 'var(--text-mid)', marginBottom: 8 }}>
                    You need to list a barter item before you can propose a trade.
                  </p>
                  <p style={{ fontSize: 12, color: 'var(--text-soft)', marginBottom: 16 }}>
                    Create your first barter listing and come back to propose this trade.
                  </p>
                  <button
                    className="btn btn-full"
                    style={{ background: '#FF9800', color: 'white', border: 'none' }}
                    onClick={() => navigate('/barter/create')}
                  >
                    + Create Your First Barter Item
                  </button>
                </div>
              )}
            </div>
          ) : user && user.id === listing.seller_id ? (
            <div style={{ background: '#E8F5E9', borderRadius: 14, padding: 20, textAlign: 'center' }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>🔄</div>
              <p style={{ fontWeight: 700, fontSize: 14 }}>This is your item</p>
              <p style={{ fontSize: 13, color: 'var(--text-soft)' }}>You'll see trade requests here</p>
            </div>
          ) : (
            <div style={{ background: '#E3F2FD', border: '2px solid #1976D2', borderRadius: 14, padding: 20, textAlign: 'center' }}>
              <button
                className="btn btn-full"
                style={{ background: '#1976D2', color: 'white', border: 'none' }}
                onClick={() => navigate(`/login?redirect=/barter/${id}`)}
              >
                Sign In to Propose Trade
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Pricing Modal */}
      {showPricingModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowPricingModal(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: 16,
              padding: 32,
              maxWidth: 600,
              width: '90%',
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
              <button
                onClick={() => setShowPricingModal(false)}
                style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', padding: 0 }}
              >
                ←
              </button>
              <div>
                <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 24, fontWeight: 700, margin: 0 }}>
                  Choose Your Option
                </h2>
                <p style={{ fontSize: 14, color: 'var(--text-soft)', margin: '4px 0 0 0' }}>
                  Select how you'd like to propose this trade
                </p>
              </div>
            </div>

            <div style={{ display: 'grid', gap: 16, marginBottom: 24 }}>
              {/* Option 1: Subscribe */}
              <div
                style={{
                  border: '2px solid #FF9800',
                  borderRadius: 12,
                  padding: 20,
                  cursor: 'pointer',
                  background: '#FFFBF0'
                }}
                onClick={() => {
                  setSelectingPaymentOption('subscribe');
                  setShowPricingModal(false);
                  setShowSubscription(true);
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>
                  💳 Subscribe for Unlimited Proposals
                </div>
                <div style={{ fontSize: 28, fontWeight: 700, color: '#FF9800', marginBottom: 8 }}>
                  $20/month
                </div>
                <ul style={{ fontSize: 13, color: 'var(--text-mid)', lineHeight: 1.8, marginBottom: 12 }}>
                  <li>✓ Unlimited trade proposals</li>
                  <li>✓ Unlimited free item claims</li>
                  <li>✓ Better value if trading frequently</li>
                </ul>
              </div>

              {/* Option 2: Pay Once */}
              <div
                style={{
                  border: '2px solid #4CAF50',
                  borderRadius: 12,
                  padding: 20,
                  cursor: 'pointer',
                  background: '#F1F8E9'
                }}
                onClick={() => {
                  setShowPricingModal(false);
                  setShowPaymentConfirm(true);
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>
                  💰 Pay Once for This Proposal
                </div>
                <div style={{ fontSize: 28, fontWeight: 700, color: '#4CAF50', marginBottom: 8 }}>
                  $10
                </div>
                <ul style={{ fontSize: 13, color: 'var(--text-mid)', lineHeight: 1.8 }}>
                  <li>✓ One-time payment</li>
                  <li>✓ No subscription needed</li>
                  <li>✓ Unlimited proposals on this item for the month</li>
                </ul>
              </div>
            </div>

            <button
              onClick={() => setShowPricingModal(false)}
              style={{
                width: '100%',
                padding: 12,
                background: 'var(--border)',
                border: 'none',
                borderRadius: 10,
                fontWeight: 700,
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Payment Confirmation Modal ($10) */}
      {showPaymentConfirm && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowPaymentConfirm(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: 16,
              padding: 32,
              maxWidth: 500,
              width: '90%',
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
              <button
                onClick={() => setShowPaymentConfirm(false)}
                style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', padding: 0 }}
              >
                ←
              </button>
              <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 24, fontWeight: 700, margin: 0 }}>
                Confirm Payment
              </h2>
            </div>

            <div style={{ background: '#F1F8E9', borderRadius: 12, padding: 20, marginBottom: 24 }}>
              <div style={{ fontSize: 13, color: 'var(--text-soft)', marginBottom: 8 }}>Item to trade for:</div>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16 }}>{listing?.title}</div>

              <div style={{ borderTop: '1px solid rgba(0,0,0,0.1)', paddingTop: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ color: 'var(--text-soft)' }}>Proposal fee</span>
                  <span style={{ fontWeight: 600 }}>$10.00</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ color: 'var(--text-soft)' }}>Duration</span>
                  <span style={{ fontWeight: 600 }}>1 month</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid rgba(0,0,0,0.1)', paddingTop: 8, marginTop: 8 }}>
                  <span style={{ fontWeight: 700 }}>Total</span>
                  <span style={{ fontWeight: 700, fontSize: 18, color: '#4CAF50' }}>$10</span>
                </div>
              </div>
            </div>

            <p style={{ fontSize: 12, color: 'var(--text-soft)', marginBottom: 20, textAlign: 'center' }}>
              You'll have unlimited proposals on this item for 30 days
            </p>

            <button
              onClick={() => {
                setShowPaymentConfirm(false);
                setSelectingPaymentOption('single');
                executeProposeTrace();
              }}
              disabled={selectingPaymentOption === 'single' && proposing}
              style={{
                width: '100%',
                padding: 14,
                background: '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: 10,
                fontWeight: 700,
                cursor: 'pointer',
                marginBottom: 12
              }}
            >
              {proposing ? 'Processing...' : '💳 Pay $10 & Propose'}
            </button>

            <button
              onClick={() => setShowPaymentConfirm(false)}
              style={{
                width: '100%',
                padding: 14,
                background: 'var(--border)',
                border: 'none',
                borderRadius: 10,
                fontWeight: 700,
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Subscription Modal */}
      <SubscriptionModal
        isOpen={showSubscription}
        onClose={() => setShowSubscription(false)}
        onSuccess={() => {
          setShowSubscription(false);
          toast.success('Subscription activated! Try proposing again.');
          executeProposeTrace();
        }}
        mode="propose"
      />

      {/* Message Modal */}
      {showMessage && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowMessage(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: 16,
              padding: 24,
              maxWidth: 500,
              width: '90%',
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, fontWeight: 700 }}>
                Message {listing.first_name}
              </h2>
              <button
                onClick={() => setShowMessage(false)}
                style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>

            <p style={{ fontSize: 13, color: 'var(--text-soft)', marginBottom: 16 }}>
              About: {listing.title}
            </p>

            <textarea
              placeholder="Ask about the item, suggest meeting times, or negotiate..."
              value={messageBody}
              onChange={e => setMessageBody(e.target.value)}
              style={{
                width: '100%',
                padding: 12,
                border: '1px solid var(--border)',
                borderRadius: 10,
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 13,
                minHeight: 120,
                marginBottom: 16,
                resize: 'none'
              }}
            />

            <button
              onClick={handleSendMessage}
              disabled={!messageBody.trim() || messageSending}
              style={{
                width: '100%',
                padding: 12,
                background: '#1976D2',
                color: 'white',
                border: 'none',
                borderRadius: 10,
                fontWeight: 700,
                cursor: messageBody.trim() ? 'pointer' : 'not-allowed',
                opacity: messageBody.trim() ? 1 : 0.5
              }}
            >
              {messageSending ? 'Sending...' : '💬 Send Message'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
